import React, { FC, useState, useEffect, Fragment } from 'react';
import { TextField, Button, MenuItem, Select, InputLabel, FormControl, Box } from '@mui/material';
import { Category, SubCategory } from 'models/organization';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';

interface AddSubCategoryDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (subCategory: SubCategory) => void;
    onDelete: (categoryId: string, subCategoryId?: string) => void;
    categories: Category[];
    selectedSubCategory: SubCategory;
}

const AddSubCategoryDialog: FC<AddSubCategoryDialogProps> = ({
    open,
    onClose,
    onSave,
    onDelete,
    categories,
    selectedSubCategory,
}) => {
    const { t: translate } = useTranslation(['common', 'manage-actions', 'client-directory']);
    const [subCategory, setSubCategory] = useState<SubCategory>(selectedSubCategory);
    const [isValid, setIsValid] = useState(true);
    const isValidHex = (value: string) => /^#([0-9A-F]{3}){1,2}$/i.test(value);

    useEffect(() => {
        setSubCategory(selectedSubCategory);
    }, [selectedSubCategory]);

    const handleChange = (field: keyof SubCategory, value: string) => {
        const updated = { ...subCategory, [field]: value };
        setSubCategory(updated);
        if (field === 'colorCode' || field === 'textColorCode') {
            setIsValid(isValidHex(value));
        }
    };

    const handleSave = () => {
        if (!subCategory.name || !subCategory.parentCategoryId || !isValid) {
            return;
        }
        onSave(subCategory);
    };

    const handleDelete = () => {
        onDelete(subCategory.parentCategoryId, subCategory?._id);
    };

    return (
        <DHDialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            title={
                subCategory?._id
                    ? translate('manage-actions:EDIT_SUB_CATEGORY')
                    : translate('manage-actions:ADD_SUB_CATEGORY')
            }
            actions={
                <Fragment>
                    {subCategory?._id && (
                        <Box mr={1}>
                            <Button onClick={handleDelete} color="error" startIcon={<DeleteIcon />}>
                                {translate('common:DELETE')}
                            </Button>
                        </Box>
                    )}
                    <Button
                        disabled={!subCategory.name || !subCategory.parentCategoryId || !isValid}
                        onClick={handleSave}
                        color="primary"
                        variant="contained"
                    >
                        {translate('common:SAVE')}
                    </Button>
                </Fragment>
            }
        >
            <Box display="flex" flexDirection="column" gap={2} mt={1}>
                <TextField
                    label={translate('manage-actions:CATEGORY_NAME')}
                    fullWidth
                    value={subCategory.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                />
                <FormControl fullWidth>
                    <InputLabel id="main-category-label">{translate('client-directory:MAIN_CATEGORY')}</InputLabel>
                    <Select
                        labelId="main-category-label"
                        value={subCategory.parentCategoryId || ''}
                        onChange={(e) => handleChange('parentCategoryId', e.target.value)}
                        label={translate('client-directory:MAIN_CATEGORY')}
                    >
                        {categories.map((cat) => (
                            <MenuItem key={cat._id} value={cat._id}>
                                {cat.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    label={translate('manage-actions:BACKGROUND_COLOR_CODE')}
                    fullWidth
                    value={subCategory.colorCode}
                    onChange={(e) => handleChange('colorCode', e.target.value)}
                    placeholder="#FFFFFF"
                    error={!!subCategory.colorCode && !isValidHex(subCategory.colorCode)}
                    helperText={!isValidHex(subCategory.colorCode) && translate('manage-actions:VALID_HEX_COLOR_CODE')}
                />
                <TextField
                    label={translate('manage-actions:TEXT_COLOR_CODE')}
                    fullWidth
                    value={subCategory.textColorCode}
                    onChange={(e) => handleChange('textColorCode', e.target.value)}
                    placeholder="#000000"
                    error={!!subCategory.textColorCode && !isValidHex(subCategory.textColorCode)}
                    helperText={
                        !isValidHex(subCategory.textColorCode) && translate('manage-actions:VALID_HEX_COLOR_CODE')
                    }
                />
            </Box>
        </DHDialog>
    );
};

export default AddSubCategoryDialog;
